import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, MagnifyingGlass, Plus, MapPin, Clock, Buildings, Star, SealCheck, UserCircle, Users, FloppyDisk } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  jobsApi, applicationsApi, EMPLOYMENT_TYPES, WORK_MODES, EXPERIENCE_LEVELS,
  INDUSTRY_OPTIONS, APPLICATION_STATUS_LABELS, formatSalary, timeAgo,
} from "@/lib/jobsApi";
import { toast } from "sonner";

/**
 * Jobs & Careers landing page — main hub for the recruitment marketplace.
 * Four tabs: Browse jobs · My postings · My applications · Career profile.
 */
export default function Jobs() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("browse");

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6" data-testid="jobs-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-blue-300">
            <Briefcase size={22} weight="fill" />
            <span className="text-xs uppercase tracking-widest font-heading font-black">Careers on Tain</span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-black mt-2">
            Jobs & Careers
          </h1>
          <p className="text-white/60 mt-2 max-w-2xl">
            Post vacancies, discover candidates, and track applications — all in one place.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => navigate("/jobs/post")}
            data-testid="jobs-post-cta"
            className="bg-blue-600 hover:bg-blue-700 gap-2"
          >
            <Plus size={16} weight="bold" /> Post a job
          </Button>
          <Button
            onClick={() => navigate("/candidates")}
            data-testid="jobs-candidates-cta"
            variant="outline"
            className="border-white/20 bg-transparent hover:bg-white/5 gap-2"
          >
            <Users size={16} weight="bold" /> Find candidates
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full grid grid-cols-4 bg-white/5">
          <TabsTrigger value="browse" data-testid="jobs-tab-browse">Browse</TabsTrigger>
          <TabsTrigger value="mine" data-testid="jobs-tab-mine">My postings</TabsTrigger>
          <TabsTrigger value="apps" data-testid="jobs-tab-apps">Applications</TabsTrigger>
          <TabsTrigger value="career" data-testid="jobs-tab-career">My career</TabsTrigger>
        </TabsList>
        <TabsContent value="browse" className="mt-4"><BrowseJobs /></TabsContent>
        <TabsContent value="mine" className="mt-4"><MyPostings /></TabsContent>
        <TabsContent value="apps" className="mt-4"><MyApplications /></TabsContent>
        <TabsContent value="career" className="mt-4"><CareerCard onEdit={() => navigate("/career")} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* -------------------- Browse Jobs -------------------- */
function BrowseJobs() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [f, setF] = useState({
    industry: "", employment_type: "", work_mode: "", experience_level: "", location: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 30 };
      if (q.trim()) params.q = q.trim();
      Object.entries(f).forEach(([k, v]) => v && (params[k] = v));
      const d = await jobsApi.list(params);
      setItems(d.items || []);
      setTotal(d.total || 0);
    } catch {
      toast.error("Failed to load jobs");
    } finally { setLoading(false); }
  }, [q, f]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => setF({ industry: "", employment_type: "", work_mode: "", experience_level: "", location: "" });

  return (
    <div className="space-y-4">
      {/* Search + filters */}
      <div className="flex flex-col md:flex-row gap-2">
        <div className="flex-1 relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" weight="bold" />
          <Input
            data-testid="jobs-search-input"
            placeholder="Search jobs by title, company, or skill…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 bg-white/5 border-white/10"
          />
        </div>
        <Input
          data-testid="jobs-filter-location"
          placeholder="Location"
          value={f.location}
          onChange={(e) => setF({ ...f, location: e.target.value })}
          className="md:w-40 bg-white/5 border-white/10"
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <PickerSelect testId="filter-employment" value={f.employment_type} onChange={(v) => setF({ ...f, employment_type: v })} placeholder="Type" options={EMPLOYMENT_TYPES} />
        <PickerSelect testId="filter-work-mode" value={f.work_mode} onChange={(v) => setF({ ...f, work_mode: v })} placeholder="Work mode" options={WORK_MODES} />
        <PickerSelect testId="filter-experience" value={f.experience_level} onChange={(v) => setF({ ...f, experience_level: v })} placeholder="Experience" options={EXPERIENCE_LEVELS} />
        <PickerSelect testId="filter-industry" value={f.industry} onChange={(v) => setF({ ...f, industry: v })} placeholder="Industry" options={INDUSTRY_OPTIONS.map((i) => ({ value: i, label: i }))} />
        {(q || Object.values(f).some(Boolean)) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setQ(""); clearFilters(); }}
            data-testid="jobs-clear-filters"
            className="text-white/60 hover:text-white"
          >
            Clear
          </Button>
        )}
      </div>

      <div className="text-xs text-white/50">
        {loading ? "Loading…" : `${total} job${total === 1 ? "" : "s"} open`}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((j) => (
          <JobCard key={j.job_id} job={j} onOpen={() => navigate(`/jobs/${j.job_id}`)} />
        ))}
      </div>
      {!loading && items.length === 0 && (
        <div className="text-center py-16 text-white/40" data-testid="jobs-empty">
          No jobs match those filters. Try clearing them or check back later.
        </div>
      )}
    </div>
  );
}

function PickerSelect({ value, onChange, placeholder, options, testId }) {
  return (
    <Select value={value || "__all"} onValueChange={(v) => onChange(v === "__all" ? "" : v)}>
      <SelectTrigger data-testid={testId} className="bg-white/5 border-white/10 w-auto min-w-[9rem]">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
        <SelectItem value="__all">{placeholder}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function JobCard({ job, onOpen }) {
  return (
    <button
      onClick={onOpen}
      data-testid={`job-card-${job.job_id}`}
      className="text-left rounded-2xl bg-white/[0.04] border border-white/10 p-5 hover:bg-white/[0.06] hover:border-white/20 transition group"
    >
      <div className="flex items-start gap-3">
        {job.employer_company_logo ? (
          <img
            src={job.employer_company_logo}
            alt={job.employer_company_name}
            className="w-12 h-12 rounded-lg object-cover bg-white/10"
          />
        ) : (
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-white/10 flex items-center justify-center">
            <Buildings size={22} className="text-white/50" weight="bold" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading font-bold text-lg leading-tight group-hover:text-blue-300 transition">
              {job.title}
            </h3>
            {job.featured && (
              <span className="px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-300 text-[10px] font-bold uppercase flex items-center gap-1">
                <Star size={10} weight="fill" /> Featured
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-white/70">
            <span>{job.employer_company_name}</span>
            {job.employer?.verified && (
              <SealCheck size={14} className="text-blue-400" weight="fill" />
            )}
          </div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
        <Chip icon={MapPin}>{job.location || "Unspecified"}</Chip>
        <Chip>{EMPLOYMENT_TYPES.find((t) => t.value === job.employment_type)?.label || job.employment_type}</Chip>
        <Chip>{WORK_MODES.find((w) => w.value === job.work_mode)?.label || job.work_mode}</Chip>
        {job.experience_level && (
          <Chip>{EXPERIENCE_LEVELS.find((e) => e.value === job.experience_level)?.label || job.experience_level}</Chip>
        )}
      </div>
      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="text-emerald-300 font-bold">{formatSalary(job.salary)}</span>
        <span className="text-white/40 flex items-center gap-1">
          <Clock size={11} weight="bold" /> {timeAgo(job.created_at)}
        </span>
      </div>
    </button>
  );
}

function Chip({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/5 border border-white/10 text-white/70">
      {Icon && <Icon size={11} weight="bold" />} {children}
    </span>
  );
}

/* -------------------- My Postings -------------------- */
function MyPostings() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await jobsApi.mine()); }
    catch { toast.error("Failed to load your postings"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await jobsApi.setStatus(id, status);
      toast.success(`Job ${status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const removeJob = async (id) => {
    if (!window.confirm("Remove this listing?")) return;
    try { await jobsApi.remove(id); toast.success("Removed"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-3">
      {loading && <div className="text-white/50">Loading…</div>}
      {!loading && items.length === 0 && (
        <Card className="bg-white/[0.03] border-white/10">
          <CardContent className="p-8 text-center">
            <Briefcase size={32} className="mx-auto text-white/30 mb-3" weight="duotone" />
            <p className="text-white/70 font-heading font-bold mb-2">You haven't posted a job yet</p>
            <p className="text-white/50 text-sm mb-4">Create your first listing to start receiving applications.</p>
            <Button onClick={() => navigate("/jobs/post")} data-testid="mine-empty-cta" className="bg-blue-600 hover:bg-blue-700 gap-2">
              <Plus size={16} weight="bold" /> Post a job
            </Button>
          </CardContent>
        </Card>
      )}
      {items.map((j) => (
        <Card key={j.job_id} className="bg-white/[0.03] border-white/10" data-testid={`my-job-${j.job_id}`}>
          <CardContent className="p-4 flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-heading font-bold text-lg">{j.title}</h3>
                <StatusPill status={j.status} />
                {j.featured && (
                  <span className="px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-300 text-[10px] font-bold uppercase flex items-center gap-1">
                    <Star size={10} weight="fill" /> Featured
                  </span>
                )}
              </div>
              <p className="text-xs text-white/50 mt-1">
                {j.application_count || 0} applications · {j.view_count || 0} views · Posted {timeAgo(j.created_at)}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/jobs/mine/${j.job_id}`)}
                data-testid={`mine-applicants-${j.job_id}`}
                className="border-white/15 bg-transparent hover:bg-white/5"
              >
                Applicants ({j.application_count || 0})
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/jobs/edit/${j.job_id}`)}
                data-testid={`mine-edit-${j.job_id}`}
                className="border-white/15 bg-transparent hover:bg-white/5"
              >
                Edit
              </Button>
              {j.status === "open" ? (
                <Button size="sm" variant="ghost" onClick={() => changeStatus(j.job_id, "paused")} data-testid={`mine-pause-${j.job_id}`}>Pause</Button>
              ) : j.status === "paused" ? (
                <Button size="sm" variant="ghost" onClick={() => changeStatus(j.job_id, "open")} data-testid={`mine-resume-${j.job_id}`}>Resume</Button>
              ) : null}
              {j.status !== "closed" && (
                <Button size="sm" variant="ghost" onClick={() => changeStatus(j.job_id, "closed")} data-testid={`mine-close-${j.job_id}`} className="text-white/60">Close</Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => removeJob(j.job_id)} data-testid={`mine-remove-${j.job_id}`} className="text-red-300 hover:text-red-200 hover:bg-red-500/10">Delete</Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    open: "bg-emerald-500/15 text-emerald-300",
    paused: "bg-yellow-500/15 text-yellow-300",
    closed: "bg-white/10 text-white/50",
    draft: "bg-blue-500/15 text-blue-300",
    removed: "bg-red-500/15 text-red-300",
  };
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-black tracking-wider ${map[status] || "bg-white/10 text-white/60"}`}>
      {status}
    </span>
  );
}

/* -------------------- My Applications -------------------- */
function MyApplications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    applicationsApi.mine().then((d) => setItems(d.items || [])).catch(() => toast.error("Failed to load applications")).finally(() => setLoading(false));
  }, []);
  return (
    <div className="space-y-3">
      {loading && <div className="text-white/50">Loading…</div>}
      {!loading && items.length === 0 && (
        <Card className="bg-white/[0.03] border-white/10">
          <CardContent className="p-8 text-center">
            <FloppyDisk size={32} className="mx-auto text-white/30 mb-3" weight="duotone" />
            <p className="text-white/70 font-heading font-bold mb-2">You haven't applied yet</p>
            <p className="text-white/50 text-sm mb-3">Browse jobs and click Apply to start tracking here.</p>
          </CardContent>
        </Card>
      )}
      {items.map((a) => (
        <Card
          key={a.application_id}
          className="bg-white/[0.03] border-white/10 cursor-pointer hover:bg-white/[0.06] transition"
          onClick={() => navigate(`/applications/${a.application_id}`)}
          data-testid={`app-${a.application_id}`}
        >
          <CardContent className="p-4 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-heading font-bold">{a.job?.title || "Job"}</h3>
                <span className="text-xs text-white/50">at {a.job?.employer_company_name}</span>
              </div>
              <p className="text-xs text-white/50 mt-1">
                Applied {timeAgo(a.created_at)}
                {a.status_note ? ` · ${a.status_note.slice(0, 80)}` : ""}
              </p>
            </div>
            <span className="px-2 py-1 rounded bg-blue-500/15 text-blue-300 text-xs font-bold uppercase">
              {APPLICATION_STATUS_LABELS[a.status] || a.status}
            </span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* -------------------- Career (mini) -------------------- */
function CareerCard({ onEdit }) {
  const [p, setP] = useState(null);
  useEffect(() => { import("@/lib/jobsApi").then(({ careerApi }) => careerApi.me().then(setP)); }, []);
  if (!p) return <div className="text-white/50">Loading…</div>;
  return (
    <Card className="bg-white/[0.03] border-white/10">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start gap-4">
          {p.photo ? (
            <img src={p.photo} alt="" className="w-16 h-16 rounded-full object-cover" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center">
              <UserCircle size={40} className="text-white/40" weight="duotone" />
            </div>
          )}
          <div className="flex-1">
            <h3 className="font-heading font-black text-xl">{p.headline || "Add a professional headline"}</h3>
            <p className="text-white/60 text-sm mt-1 line-clamp-2">
              {p.summary || "Add a short summary so employers know what you do."}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 text-center text-xs">
          <Stat label="Views" value={p.profile_views || 0} />
          <Stat label="Skills" value={(p.skills || []).length} />
          <Stat label="Experience" value={(p.experience || []).length} />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={onEdit} data-testid="career-edit-cta" className="bg-blue-600 hover:bg-blue-700 gap-2">
            <UserCircle size={16} weight="bold" /> Edit my profile
          </Button>
          {p.cv_url ? (
            <a href={p.cv_url} target="_blank" rel="noreferrer" className="text-blue-300 hover:underline text-sm self-center">
              View uploaded CV ({p.cv_filename || "cv"})
            </a>
          ) : (
            <span className="text-yellow-300 text-sm self-center">📄 Upload your CV to apply</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }) {
  return (
    <div className="p-3 rounded-lg bg-white/5">
      <div className="text-2xl font-heading font-black">{value}</div>
      <div className="text-white/40 uppercase tracking-widest text-[10px] font-bold mt-1">{label}</div>
    </div>
  );
}
